import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ReCaptchaV3Service } from 'ngx-captcha';
import { map } from 'rxjs';
import { IConfig } from 'src/app/core/interfaces/config.interface';
import { ILookup } from 'src/app/core/interfaces/lookup.interface';
import { ConfigService } from 'src/app/services/config.service';
import { DataService } from 'src/app/services/data.service';
import { FormService } from 'src/app/services/form.service';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-visitor',
  templateUrl: './visitor.component.html',
  styleUrls: ['./visitor.component.scss'],
})
export class VisitorComponent implements OnInit {
  inductionForm: FormGroup;
  public sites: ILookup[] = [];
  public objOther: ILookup = {
    id: 'other',
    name: 'Other',
  };
  config: IConfig;

  siteKey: string;
  isSubmitted: boolean;
  minDate: any;
  isOtherSite: Boolean = false;

  // Custom validator to ensure mobile numbers are different
  static phoneNumbersValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const mobileNo = control.get('mobileNo')?.value;
    const alecContactPhone = control.get('alecContactPhone')?.value;

    if (mobileNo && alecContactPhone && mobileNo === alecContactPhone) {
      return { samePhoneNumbers: true };
    }
    return null;
  }

  // Custom validator to prevent ALEC in company name
  static companyNameValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    if (value && value.toLowerCase().includes('alec')) {
      return { containsAlec: true };
    }
    return null;
  }

  constructor(
    private formBuilder: FormBuilder,
    private dataSvc: DataService,
    private formSvc: FormService,
    private router: Router,
    private recaptchaSvc: ReCaptchaV3Service,
    private storageSvc: StorageService,
    private configSvc: ConfigService
  ) {
    this.configSvc.setConfiguration(location.hostname, location.pathname);
    this.storageSvc.configValue.subscribe((x) => (this.config = x));

    this.siteKey = environment.recaptchaSiteKey;
    const datePipe = new DatePipe('en-Us');
    this.minDate = datePipe.transform(new Date(), 'yyyy-MM-dd');

    this.inductionForm = this.formBuilder.group(
      {
        site: [null, Validators.required],
        otherSite: [null],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],

        mobileNo: [
          '',
          [
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.minLength(8),
          ],
        ],
        email: [
          '',
          [
            Validators.required,
            Validators.pattern(
              /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
            ),
          ],
        ],
        company: [
          '',
          [Validators.required, VisitorComponent.companyNameValidator],
        ],
        position: ['', Validators.required],

        visitTime: ['', Validators.required],
        visitDateTime: ['', Validators.required],

        emergencyContactName: ['', Validators.required],
        emergencyContactPhone: [
          '',
          [
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.minLength(8),
          ],
        ],

        alecContactName: ['', Validators.required],
        alecContactPhone: [
          '',
          [
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.minLength(8),
          ],
        ],
        alecContactEmail: [
          '',
          [
            Validators.required,
            Validators.pattern(
              /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
            ),
          ],
        ],
        purposeOfVisit: ['', Validators.required],

        isShareAgree: [false, Validators.requiredTrue],
        isSafetyAgree: [false, Validators.requiredTrue],

        recaptcha: ['', Validators.required], //,
      },
      { validators: VisitorComponent.phoneNumbersValidator }
    );

    this.dataSvc
      .getSites(this.config?.id)
      .pipe(
        map((x: ILookup[]) => x.filter((x) => x.name != null && x.name != ''))
      )
      .subscribe((x) => {
        this.sites = x;

        // if (this.sites.length > 0) {
        //   this.sites.push(this.objOther);
        // }
      });
  }

  ngOnInit(): void {
    this.setSiteValidator();
    this.setupServerErrorHandling();
  }

  private setupServerErrorHandling(): void {
    // Clear server errors when user starts typing
    this.inductionForm.get('email')?.valueChanges.subscribe(() => {
      const control = this.inductionForm.get('email');
      if (control?.errors?.['serverError']) {
        const errors = { ...control.errors };
        delete errors['serverError'];
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    });

    this.inductionForm.get('alecContactEmail')?.valueChanges.subscribe(() => {
      const control = this.inductionForm.get('alecContactEmail');
      if (control?.errors?.['serverError']) {
        const errors = { ...control.errors };
        delete errors['serverError'];
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    });

    this.inductionForm.get('company')?.valueChanges.subscribe(() => {
      const control = this.inductionForm.get('company');
      if (control?.errors?.['serverError']) {
        const errors = { ...control.errors };
        delete errors['serverError'];
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    });
  }

  public setSiteValidator() {
    this.inductionForm.get('site').valueChanges.subscribe((x) => {
      if (x == 'Other') {
        this.inductionForm
          .get('otherSite')
          .setValidators([Validators.required]);
      } else {
        this.inductionForm.get('otherSite').setValidators(null);
      }

      this.inductionForm.get('otherSite').updateValueAndValidity();
    });
  }

  public onSiteChange(e) {
    let site = e.name;

    if (site == 'Other') this.isOtherSite = true;
    else {
      this.isOtherSite = false;
      this.inductionForm.patchValue({ otherSite: null }, { onlySelf: true });
    }
  }

  public submitInduction(): void {
    var induction = this.inductionForm.value;

    if (this.inductionForm.valid) {
      console.log(induction);
      this.isSubmitted = true;

      induction.visitDateTime = this.setDateTime(
        induction.visitDateTime,
        induction.visitTime
      );

      this.formSvc.submitInduction(this.config?.id, induction).subscribe({
        next: async (response) => {
          console.log(response);
          if (response && response.success) {
            this.router.navigate([this.config.url + '/thank-you'], {
              queryParams: { response: JSON.stringify(response) },
            });
          } else if (!response.success && response.message) {
            alert(response.message);
            this.isSubmitted = false;
          }
        },
        error: (error) => {
          console.error('Submission error:', error);
          this.isSubmitted = false;

          // Handle specific validation errors from server
          if (
            error.status === 400 &&
            error.error &&
            error.error.validationErrors
          ) {
            const validationErrors = error.error.validationErrors;

            // Check for email validation error
            if (validationErrors.email) {
              this.inductionForm
                .get('email')
                ?.setErrors({ serverError: validationErrors.email });
            }

            // Check for Alec contact email validation error
            if (validationErrors.alecContactEmail) {
              this.inductionForm
                .get('alecContactEmail')
                ?.setErrors({ serverError: validationErrors.alecContactEmail });
            }

            // Check for company validation error
            if (validationErrors.company) {
              this.inductionForm
                .get('company')
                ?.setErrors({ serverError: validationErrors.company });
            }
          } else if (error.error && error.error.message) {
          } else {
          }

          this.isSubmitted = false;
        },
      });
    }
  }

  private setDateTime(date: string, time: string): Date {
    var d = new Date(date);

    if (time) {
      d.setHours(parseInt(time.split(':')[0]));
      d.setMinutes(parseInt(time.split(':')[1]));
    }

    return d;
  }
}
